import { useState, useEffect, useRef, useCallback } from "react";

import { doc, updateDoc } from "firebase/firestore";

import useLesson from "@/contexts/useLesson";
import { UserRole } from "@/types";
import { db } from "@/utils/firebase";

import useFirebaseContext from "./useFirebaseAuth";

interface AIAssistantSuggestion {
  id: string;
  type: "clarification" | "example" | "encouragement" | "concept" | "problem";
  message: string;
  timestamp: Date;
  context?: string;
}

interface TranscriptEntry {
  text: string;
  speaker: 'Tutor' | 'Student';
  timestamp: Date;
}

export default function useAITeachingAssistant(
  transcripts: TranscriptEntry[],
  isEnabled: boolean
) {
  const [suggestions, setSuggestions] = useState<AIAssistantSuggestion[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { aiAxios, userRole } = useFirebaseContext();
  const { lesson, lessonInteractionProfile } = useLesson();

  const transcriptBufferRef = useRef<TranscriptEntry[]>([]);
  const newTranscriptsBatchRef = useRef<string[]>([]);
  const lastAnalysisRef = useRef<Date>(new Date());
  const lastAnalysisConversationRef = useRef<string>("");
  const analysisIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const unsavedTranscriptsRef = useRef<TranscriptEntry[]>([]);
  const lastProcessedLengthRef = useRef<number>(0);

  // Only run on tutor side (host)
  const shouldRunAnalysis = userRole === UserRole.Tutor && isEnabled;

  // Add new transcripts to buffer and batch
  useEffect(() => {
    if (transcripts.length > 0) {
      const latestTranscript = transcripts[transcripts.length - 1];
      const lastEntry =
        transcriptBufferRef.current[transcriptBufferRef.current.length - 1];
      if (lastEntry && lastEntry.text === latestTranscript.text && lastEntry.speaker === latestTranscript.speaker) {
        return;
      }

      if (newTranscriptsBatchRef.current.includes(latestTranscript.text)) {
        return;
      }

      transcriptBufferRef.current.push(latestTranscript);
      newTranscriptsBatchRef.current.push(latestTranscript.text);

      if (transcripts.length > lastProcessedLengthRef.current) {
        const newEntries = transcripts.slice(lastProcessedLengthRef.current);

        newEntries.forEach((entry) => {
          const exists = unsavedTranscriptsRef.current.some(
            (e) => e.text === entry.text && e.speaker === entry.speaker && e.timestamp.getTime() === entry.timestamp.getTime()
          );
          if (!exists) {
            unsavedTranscriptsRef.current.push(entry);
          }
        });
        lastProcessedLengthRef.current = transcripts.length;
      }

      // Clean buffer - keep only last 90 seconds
      const cutoffTime = new Date(Date.now() - 90000);
      transcriptBufferRef.current = transcriptBufferRef.current.filter(
        (t) => t.timestamp > cutoffTime
      );
    }
  }, [transcripts]);

  const updateFirestoreTranscript = useCallback(async () => {
    if (!lessonInteractionProfile?.id || unsavedTranscriptsRef.current.length === 0) {
      return;
    }

    try {
      const transcriptText = unsavedTranscriptsRef.current
        .map((entry) => `${entry.speaker}: ${entry.text}`)
        .join('\n');

      const lessonInteractionProfileRef = doc(
        db,
        "lessonInteractionProfiles",
        lessonInteractionProfile.id
      );

      const currentTranscript = lessonInteractionProfile.transcript || "";
      const updatedTranscript = currentTranscript
        ? `${currentTranscript}\n${transcriptText}`
        : transcriptText;

      await updateDoc(lessonInteractionProfileRef, {
        transcript: updatedTranscript,
      });

      unsavedTranscriptsRef.current = [];
    } catch (error) {
      console.error("Error updating transcript in Firestore:", error);
    }
  }, [lessonInteractionProfile]);

  const analyzeConversation = useCallback(async () => {
    const timeSinceLastAnalysis =
      Date.now() - lastAnalysisRef.current.getTime();
    const currentTranscriptCount = transcriptBufferRef.current.length;

    if (
      currentTranscriptCount < 5 ||
      timeSinceLastAnalysis < 30000 || // Min 30 seconds between analyses
      isAnalyzing
    ) {
      return;
    }

    // Format conversation text with speaker labels
    const conversationText = transcriptBufferRef.current
      .map((entry) => `${entry.speaker}: ${entry.text}`)
      .join("\n")
      .trim();

    // Skip if conversation is too short
    if (
      conversationText.split(" ").length < 20 ||
      conversationText === lastAnalysisConversationRef.current
    ) {
      return;
    }

    // Clear the batch after using it for analysis
    newTranscriptsBatchRef.current = [];

    setIsAnalyzing(true);
    lastAnalysisRef.current = new Date();
    lastAnalysisConversationRef.current = conversationText; // Don't analyze if no new transcripts since last analysis

    try {
      await updateFirestoreTranscript();

      const response = await aiAxios.post("luna/analyze-conversation", {
        lessonId: lesson?.id,
        conversation: conversationText,
        subjects: lesson?.subjects || [],
        recentProblems:
          lessonInteractionProfile?.realtimeResources?.problems?.slice(-3) ||
          [],
        timestamp: new Date().toISOString(),
      });

      if (response.data.suggestion) {
        const newSuggestion: AIAssistantSuggestion = {
          id: Date.now().toString(),
          ...response.data.suggestion,
          timestamp: new Date(),
        };

        setSuggestions((prev) => [...prev, newSuggestion]);

        // Auto-dismiss after 30 seconds
        setTimeout(() => {
          setSuggestions((prev) =>
            prev.filter((s) => s.id !== newSuggestion.id)
          );
        }, 30000);
      }
    } catch (error) {
      console.error("AI analysis error:", error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [aiAxios, lesson, isAnalyzing, lessonInteractionProfile, updateFirestoreTranscript]);

  // Set up periodic analysis
  useEffect(() => {
    if (shouldRunAnalysis) {
      // Run analysis every 45 seconds
      analysisIntervalRef.current = setInterval(() => {
        analyzeConversation();
      }, 45000);

      return () => {
        if (analysisIntervalRef.current) {
          clearInterval(analysisIntervalRef.current);
        }
      };
    }
  }, [shouldRunAnalysis, analyzeConversation]);

  const dismissSuggestion = useCallback((id: string) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return {
    suggestions,
    isAnalyzing,
    dismissSuggestion,
  };
}
